import React, { useRef, useState } from "react";

import { Container } from "@components/Container/Container";
import styles from "./styles.module.scss";
import { Box } from "@components/Box/Box";
import { ImageField } from "@helpers/interfaces/image";
import classnames from "classnames";
import { UmbracoImage } from "@components/Image/UmbracoImage/UmbracoImage";
import anime from "animejs";
import { Icon } from "@components/Icon/Icon";
import arrowDown from "@svg-icons/arrow-down.component.svg";
import { Heading } from "@components/Heading/Heading";
import { forceCheck } from "react-lazyload";
import { Module } from "@components/Module/Module";
interface responseProps {
  Key: string;
  Image: ImageField;
  Text: string;
}

interface subFaqProps {
  Key: string;
  Question: string;
  Response: responseProps[];
}
interface faqProps extends subFaqProps {
  GlobalFaqElementChosen?: subFaqProps;
}

export interface FaqProps {
  faqs: faqProps[];
  title?: string;
}

const Faq: React.FC<FaqProps> = ({ faqs, title, ...props }) => {
  const [selectedAccordion, setSelectedAccordion] = useState<number>(0);
  const accordionBox = useRef<HTMLDivElement[]>([]);

  const animateAccordion = (target: HTMLDivElement, open: boolean): void => {
    anime({
      targets: target,
      translateY: open ? [-60, 0] : [0, -60],
      height: !open ? 0 : null,
      opacity: open ? [0, 1] : [1, 0],
      duration: 200,
      easing: open ? "easeOutQuart" : "easeInQuart",
      begin: () => {
        if (open) {
          target.removeAttribute("hidden");
          target.style.height = "auto";

          forceCheck();
        }
      },
      complete: () => {
        if (!open) {
          target.style.height = "0px";
          target.setAttribute("hidden", "");
        }
      }
    });
  };

  const toggle: React.MouseEventHandler<HTMLButtonElement> = e => {
    const key = e.currentTarget.dataset.key
      ? parseInt(e.currentTarget.dataset.key)
      : 0;

    if (selectedAccordion !== key) {
      if (selectedAccordion > 0) {
        animateAccordion(accordionBox.current[selectedAccordion - 1], false);
      }
      animateAccordion(accordionBox.current[key - 1], true);
      setSelectedAccordion(key);
    } else {
      animateAccordion(accordionBox.current[key - 1], false);
      setSelectedAccordion(0);
    }
  };

  return (
    <Module animate {...props}>
      <Container variant="large">
        {title && (
          <Heading as="h2" className={styles.heading}>
            {title}
          </Heading>
        )}

        <Box className={classnames(styles.faqList)}>
          {faqs &&
            faqs.map((faq: faqProps, index: number) => {
              const keyIndex = index + 1;
              const faqData = faq.GlobalFaqElementChosen || faq;
              return (
                <Box
                  key={faqData.Key}
                  className={classnames(
                    styles.faqListRow,
                    keyIndex === selectedAccordion && styles.faqListRowOpen
                  )}
                >
                  {faqData.Question && (
                    <button
                      className={classnames(
                        styles.button,
                        keyIndex === selectedAccordion && styles.buttonOpen
                      )}
                      data-key={keyIndex}
                      onClick={toggle}
                      id={`acid_${faqData.Key}`}
                      aria-controls={`controlid_${faqData.Key}`}
                      aria-expanded={selectedAccordion === keyIndex}
                    >
                      {faqData.Question}
                      <Icon svg={arrowDown} />
                    </button>
                  )}
                  {faqData.Response &&
                    faqData.Response.map((respon: responseProps) => {
                      return (
                        <div
                          key={respon.Key}
                          className={classnames(
                            styles.response,
                            respon.Image &&
                              keyIndex === selectedAccordion &&
                              styles.responseImage
                          )}
                          id={`controlid_${faq.Key}`}
                          aria-labelledby={`acid_${faq.Key}`}
                          hidden
                          ref={(el: HTMLDivElement): HTMLDivElement =>
                            (accordionBox.current[index] = el)
                          }
                        >
                          <Box
                            className={classnames(
                              styles.content,
                              !respon.Image && styles.contentNoimage
                            )}
                          >
                            {respon.Image && (
                              <Box className={styles.img}>
                                <UmbracoImage
                                  image={respon.Image}
                                  width={[350, 400, 400, 400]}
                                />
                              </Box>
                            )}
                            {respon.Text && (
                              <Box
                                className={styles.bodyText}
                                dangerouslySetInnerHTML={{
                                  __html: respon.Text
                                }}
                              />
                            )}
                          </Box>
                        </div>
                      );
                    })}
                </Box>
              );
            })}
        </Box>
      </Container>
    </Module>
  );
};

export { Faq };
